import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Check, Minus, Plus, TicketPercent, X,
} from 'lucide-react';

import { createCoupon, loadCoupons, updateCoupon } from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, XomicsSelect,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { Switch } from './ProductOptions';
import './productDetail.css';
import './coupons.css';

// The storefront's coupons: a card each, and a sheet like the product sheet
// (XomicsProducts.js) to make or change one, with its on/off switch on top.
// A coupon works on the whole store or on chosen top-level categories and
// products, the only scopes checkout reads (CouponService.js), in every
// currency the store sells in unless narrowed.

function currencyByCode(currencies, code) {
  return currencies.find((entry) => entry.code === code) || null;
}

function major(minorValue, currency) {
  return Number(minorValue || 0) / (10 ** currency.minorUnit);
}

function minorOf(value, currency) {
  return Math.round(Number(value) * (10 ** currency.minorUnit));
}

function money(minorValue, currency) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.code })
    .format(major(minorValue, currency));
}

// Dates are whole days in the browser's time zone: a coupon starts at the
// start of its first day and stops at the end of its last (expiresAt is the
// first moment it no longer works).
function dayOf(value, { last = false } = {}) {
  if (value === null || value === undefined) return '';
  const date = new Date(Number(value) - (last ? 1 : 0));
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function momentOf(day, { last = false } = {}) {
  if (day === '') return null;
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date + (last ? 1 : 0)).getTime();
}

function dayLabel(value, { last = false } = {}) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
    .format(new Date(Number(value) - (last ? 1 : 0)));
}

function isTopLevel(category) {
  return category.parentId === null || category.parentId === undefined;
}

function discountLabel(coupon, currencies) {
  if (coupon.discountType === 'percent') {
    return `${(coupon.discountBasisPoints / 100).toFixed(2).replace(/\.00$/, '')}%`;
  }
  const first = coupon.markets.find((market) => market.currency === 'EUR') || coupon.markets[0];
  const currency = first ? currencyByCode(currencies, first.currency) : null;
  if (!currency) return '';
  const more = coupon.markets.length - 1;
  return `${money(first.discountAmountMinor, currency)}${more > 0 ? ` +${more}` : ''}`;
}

function scopeLabel(coupon, productNames, categoryNames) {
  if (coupon.productIds.length === 0 && coupon.categoryIds.length === 0) {
    return tx('ui.products.coupons.all_products');
  }
  return [
    ...coupon.categoryIds.map((id) => categoryNames.get(id) || `#${id}`),
    ...coupon.productIds.map((id) => productNames.get(id) || `#${id}`),
  ].join(', ');
}

function usageLabel(coupon) {
  return coupon.maximumRedemptions === null
    ? String(coupon.redemptionCount)
    : `${coupon.redemptionCount} / ${coupon.maximumRedemptions}`;
}

function marketRow(market, currency) {
  return {
    currency: currency.code,
    amount: market.discountAmountMinor === null ? '' : String(major(market.discountAmountMinor, currency)),
    minimum: Number(market.minimumSubtotalMinor) === 0 ? '' : String(major(market.minimumSubtotalMinor, currency)),
  };
}

// The sheet's form. A new coupon works in every currency the store sells in;
// a stored one keeps the currencies it has that are still sold in. Whether a
// stored coupon is on is its switch's, not the form's.
function formOf(coupon, currencies) {
  const active = currencies.filter((currency) => currency.isActive);
  if (!coupon) {
    return {
      code: '', name: '', discountType: 'percent', percent: '10',
      markets: active.map((currency) => ({ currency: currency.code, amount: '', minimum: '' })),
      startsOn: '', endsOn: '', maximum: '',
      wholeStore: true, productIds: [], categoryIds: [], requirements: [], isActive: true,
    };
  }
  return {
    code: coupon.code,
    name: coupon.name,
    discountType: coupon.discountType,
    percent: coupon.discountType === 'percent' ? String(coupon.discountBasisPoints / 100) : '10',
    markets: coupon.markets.flatMap((market) => {
      const currency = currencyByCode(active, market.currency);
      return currency ? [marketRow(market, currency)] : [];
    }),
    startsOn: dayOf(coupon.startsAt),
    endsOn: dayOf(coupon.expiresAt, { last: true }),
    maximum: coupon.maximumRedemptions === null ? '' : String(coupon.maximumRedemptions),
    wholeStore: coupon.productIds.length === 0 && coupon.categoryIds.length === 0,
    productIds: coupon.productIds,
    categoryIds: coupon.categoryIds,
    requirements: coupon.requirements.map((rule) => ({
      ...rule, minimumQuantity: String(rule.minimumQuantity),
    })),
  };
}

// The currencies a coupon works in: a percentage in every ticked one, a fixed
// amount in every one given an amount.
function offeredRows(form) {
  return form.discountType === 'fixed' ? form.markets.filter((row) => row.amount !== '') : form.markets;
}

// The coupon as XOmics takes it (Coupons.js readCouponBody). Throws the
// message the sheet shows. A date left as it was keeps its stored moment.
function bodyOf(form, coupon, currencies, isActive) {
  const percent = Number(form.percent);
  if (form.discountType === 'percent' && (!Number.isFinite(percent) || percent <= 0 || percent > 100)) {
    throw new Error(tx('ui.products.coupons.invalid_discount'));
  }
  const maximum = form.maximum === '' ? null : Number(form.maximum);
  if (maximum !== null && (!Number.isSafeInteger(maximum) || maximum < 1)) {
    throw new Error(tx('ui.products.coupons.invalid_limits'));
  }
  const markets = offeredRows(form).map((row) => {
    const currency = currencyByCode(currencies, row.currency);
    const minimum = row.minimum === '' ? 0 : Number(row.minimum);
    const amount = row.amount === '' ? NaN : Number(row.amount);
    return {
      currency: row.currency,
      discountAmountMinor: form.discountType === 'fixed' && Number.isFinite(amount) ? minorOf(amount, currency) : null,
      minimumSubtotalMinor: Number.isFinite(minimum) ? minorOf(minimum, currency) : null,
    };
  });
  if (markets.length === 0 || markets.some((market) => (
    !Number.isSafeInteger(market.minimumSubtotalMinor) || market.minimumSubtotalMinor < 0
    || (form.discountType === 'fixed'
      && (!Number.isSafeInteger(market.discountAmountMinor) || market.discountAmountMinor < 1))
  ))) {
    throw new Error(tx('ui.products.coupons.invalid_markets'));
  }
  if (!form.wholeStore && form.productIds.length === 0 && form.categoryIds.length === 0) {
    throw new Error(tx('ui.products.coupons.choose_scope'));
  }
  const requirements = form.requirements.map((rule) => ({
    type: rule.type,
    minimumQuantity: Number(rule.minimumQuantity),
    productId: rule.type === 'product_quantity' ? Number(rule.productId) : null,
    categoryId: rule.type === 'category_quantity' ? Number(rule.categoryId) : null,
  }));
  if (requirements.some((rule) => !Number.isSafeInteger(rule.minimumQuantity)
      || rule.minimumQuantity < 1
      || (rule.type === 'product_quantity' && !Number.isSafeInteger(rule.productId))
      || (rule.type === 'category_quantity' && !Number.isSafeInteger(rule.categoryId)))) {
    throw new Error(tx('ui.products.coupons.invalid_requirements'));
  }
  const stored = coupon ? formOf(coupon, currencies) : null;
  const startsAt = stored && form.startsOn === stored.startsOn ? coupon.startsAt : momentOf(form.startsOn);
  const expiresAt = stored && form.endsOn === stored.endsOn
    ? coupon.expiresAt : momentOf(form.endsOn, { last: true });
  const details = {
    ...(coupon ? {} : { code: form.code.trim().toUpperCase() }),
    name: form.name.trim(),
    discountType: form.discountType,
    discountBasisPoints: form.discountType === 'percent' ? Math.round(percent * 100) : null,
    markets,
    startsAt,
    expiresAt,
    maximumRedemptions: maximum,
    isActive,
    productIds: form.wholeStore ? [] : form.productIds,
    categoryIds: form.wholeStore ? [] : form.categoryIds,
    requirements,
  };
  if (!details.name || (!coupon && !details.code)
      || (startsAt !== null && expiresAt !== null && startsAt >= expiresAt)) {
    throw new Error(tx('ui.products.coupons.complete_fields'));
  }
  return details;
}

function Choice({ value, options, onChange, label, disabled }) {
  return <div className="xo-coupon-choice" role="radiogroup" aria-label={label}>
    {options.map((option) => <button type="button" role="radio" key={option.value}
      aria-checked={value === option.value} className={value === option.value ? 'is-on' : ''}
      disabled={disabled} onClick={() => onChange(option.value)}>{option.label}</button>)}
  </div>;
}

function CouponSheet({
  coupon, products, categories, currencies, mayManage, onClose, onSaved, onSwitched,
}) {
  const active = useMemo(() => currencies.filter((currency) => currency.isActive), [currencies]);
  const initial = useMemo(() => formOf(coupon, currencies), [coupon, currencies]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [currenciesOpen, setCurrenciesOpen] = useState(false);
  const locked = busy || !mayManage;
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const isActive = coupon ? coupon.isActive : form.isActive;
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  // Top-level categories are what checkout matches; a subcategory a coupon
  // already names stays listed so it can be cleared.
  const scopeCategories = categories.filter((category) => (
    (isTopLevel(category) && category.isActive) || form.categoryIds.includes(category.id)
  ));
  const ruleCategories = (keepId) => categories.filter((category) => (
    (isTopLevel(category) && category.isActive) || category.id === keepId
  ));
  const scopeProducts = products.filter((product) => (
    product.isActive || form.productIds.includes(product.id)
  ));

  const close = useCallback(() => {
    if (busy) return;
    if (dirty && coupon && !window.confirm(tx('ui.products.detail.discard_changes'))) return;
    onClose();
  }, [busy, coupon, dirty, onClose]);

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [close]);

  function toggleId(field, id) {
    set(field, form[field].includes(id)
      ? form[field].filter((candidate) => candidate !== id)
      : [...form[field], id]);
  }

  function toggleCurrency(currency) {
    set('markets', form.markets.some((row) => row.currency === currency.code)
      ? form.markets.filter((row) => row.currency !== currency.code)
      : [...form.markets, { currency: currency.code, amount: '', minimum: '' }]);
  }

  // A fixed coupon's currency joins when its amount is typed.
  function changeMarket(code, changes) {
    set('markets', form.markets.some((row) => row.currency === code)
      ? form.markets.map((row) => (row.currency === code ? { ...row, ...changes } : row))
      : [...form.markets, { currency: code, amount: '', minimum: '', ...changes }]);
  }

  function addAllCurrencies() {
    const chosen = new Set(form.markets.map((row) => row.currency));
    set('markets', [...form.markets, ...active.filter((currency) => !chosen.has(currency.code))
      .map((currency) => ({ currency: currency.code, amount: '', minimum: '' }))]);
  }

  function changeRule(index, changes) {
    set('requirements', form.requirements.map((rule, ruleIndex) => (
      ruleIndex === index ? { ...rule, ...changes } : rule
    )));
  }

  async function toggleActive() {
    if (!coupon) {
      set('isActive', !form.isActive);
      return;
    }
    setBusy(true); setError('');
    try {
      await updateCoupon(coupon.id, bodyOf(formOf(coupon, currencies), coupon, currencies, !coupon.isActive));
      onSwitched();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    let details;
    try {
      details = bodyOf(form, coupon, currencies, isActive);
    } catch (invalid) {
      setError(invalid.message);
      return;
    }
    setBusy(true);
    try {
      if (coupon) await updateCoupon(coupon.id, details);
      else await createCoupon(details);
      onSaved(coupon ? tx('ui.products.coupons.updated') : tx('ui.products.coupons.created'));
    } catch (requestError) {
      setError(errorMessage(requestError));
      setBusy(false);
    }
  }

  const offered = offeredRows(form).length;
  const currencySummary = offered === active.length
    ? tx('ui.products.coupons.currencies_all', { count: active.length })
    : tx('ui.products.coupons.currencies_some', { count: offered, total: active.length });
  const fixed = form.discountType === 'fixed';
  const showCurrencies = currenciesOpen || fixed;
  const scope = coupon ? scopeLabel(
    coupon,
    new Map(products.map((product) => [product.id, product.name])),
    new Map(categories.map((category) => [category.id, category.name])),
  ) : '';

  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
    <section className="xo-pm xo-coupon-sheet" role="dialog" aria-modal="true"
      aria-labelledby="coupon-sheet-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="xo-pm-head">
        <span className="xo-pm-thumb"><TicketPercent aria-hidden="true" /></span>
        <div className="xo-pm-title">
          <h2 id="coupon-sheet-title">{coupon ? coupon.code : tx('ui.products.coupons.new_title')}</h2>
          <p>{coupon
            ? <><span>{coupon.name}</span><Pill tone="info">{discountLabel(coupon, currencies)}</Pill></>
            : <span>{tx('ui.products.coupons.editor_detail')}</span>}</p>
        </div>
        <div className="xo-pm-head-actions">
          {mayManage ? (
            <div className={`xo-pm-sale${isActive ? ' is-on' : ''}`}>
              <span>
                <strong>{isActive ? tx('ui.products.coupons.enabled') : tx('ui.products.coupons.disabled')}</strong>
                <small>{isActive
                  ? tx('ui.products.coupons.customers_can_use') : tx('ui.products.coupons.customers_cannot_use')}</small>
              </span>
              <Switch on={isActive} disabled={busy} label={tx('ui.products.coupons.active')} onChange={toggleActive} />
            </div>
          ) : (
            <Pill tone={isActive ? 'ok' : 'flat'}>{isActive
              ? tx('ui.products.coupons.enabled') : tx('ui.products.coupons.disabled')}</Pill>
          )}
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.products.coupons.close')} onClick={close}>
            <X aria-hidden="true" />
          </button>
        </div>
      </header>

      <form className="xo-pm-body xo-coupon-sheet-body" onSubmit={submit}>
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}

        {coupon && <div className="xo-pm-facts">
          <div className="xo-pm-fact">
            <span>{tx('ui.products.coupons.offer')}</span>
            <strong>{discountLabel(coupon, currencies)}</strong>
            <small>{coupon.discountType === 'percent'
              ? tx('ui.products.coupons.percentage') : tx('ui.products.coupons.fixed')}</small>
          </div>
          <div className="xo-pm-fact">
            <span>{tx('ui.products.coupons.usage')}</span>
            <strong>{usageLabel(coupon)}</strong>
            <small>{tx('ui.products.coupons.reserved')}: {coupon.reservedCount}</small>
          </div>
          <div className="xo-pm-fact">
            <span>{tx('ui.products.coupons.works_on')}</span>
            <strong title={scope}>{coupon.productIds.length === 0 && coupon.categoryIds.length === 0
              ? tx('ui.products.coupons.whole_store') : scope}</strong>
            <small>{tx('ui.products.coupons.currency_count', { count: coupon.markets.length })}</small>
          </div>
          <div className="xo-pm-fact">
            <span>{tx('ui.products.coupons.ends')}</span>
            <strong>{coupon.expiresAt === null
              ? tx('ui.products.coupons.no_end') : dayLabel(coupon.expiresAt, { last: true })}</strong>
            <small>{coupon.startsAt === null
              ? '' : tx('ui.products.coupons.starts_on', { date: dayLabel(coupon.startsAt) })}</small>
          </div>
        </div>}

        <section className="xo-pm-card">
          <h3>{tx('ui.products.coupons.offer')}</h3>
          <div className="xo-product-details-grid">
            <Field id="coupon-code" label={tx('ui.products.coupons.code')}>
              <input {...fieldAria('coupon-code')} type="text" value={form.code} maxLength="64"
                disabled={locked || Boolean(coupon)} placeholder="WELCOME10"
                onChange={(event) => set('code', event.target.value.toUpperCase())} />
            </Field>
            <Field id="coupon-name" label={tx('ui.products.coupons.name')}>
              <input {...fieldAria('coupon-name')} type="text" value={form.name} maxLength="255" disabled={locked}
                placeholder={tx('ui.products.coupons.name_placeholder')}
                onChange={(event) => set('name', event.target.value)} />
            </Field>
            <div className="xomics-field">
              <span className="xo-coupon-label">{tx('ui.products.coupons.type')}</span>
              <Choice value={form.discountType} disabled={locked} label={tx('ui.products.coupons.type')}
                onChange={(value) => set('discountType', value)} options={[
                  { value: 'percent', label: tx('ui.products.coupons.percentage') },
                  { value: 'fixed', label: tx('ui.products.coupons.fixed') },
                ]} />
            </div>
            {form.discountType === 'percent' ? (
              <Field id="coupon-percent" label={tx('ui.products.coupons.discount_percent')}>
                <span className="xo-coupon-suffix">
                  <input {...fieldAria('coupon-percent')} type="number" min="0.01" max="100" step="0.01"
                    value={form.percent} disabled={locked} onChange={(event) => set('percent', event.target.value)} />
                  <em aria-hidden="true">%</em>
                </span>
              </Field>
            ) : <p className="xo-pm-hint xo-coupon-fixed-hint">{tx('ui.products.coupons.fixed_hint')}</p>}
          </div>
        </section>

        <section className="xo-pm-card">
          <h3>{tx('ui.products.coupons.works_on')}</h3>
          <Choice value={form.wholeStore ? 'all' : 'chosen'} disabled={locked} label={tx('ui.products.coupons.works_on')}
            onChange={(value) => set('wholeStore', value === 'all')} options={[
              { value: 'all', label: tx('ui.products.coupons.whole_store') },
              { value: 'chosen', label: tx('ui.products.coupons.chosen') },
            ]} />
          {!form.wholeStore && <div className="xo-coupon-scope">
            <div>
              <h4>{tx('ui.products.coupons.categories')}</h4>
              <div className="xo-coupon-checks">{scopeCategories.map((category) => (
                <label key={category.id}>
                  <input type="checkbox" checked={form.categoryIds.includes(category.id)} disabled={locked}
                    onChange={() => toggleId('categoryIds', category.id)} />
                  <span><strong>{category.name}</strong>
                    <small>{category.productCount} {tx('ui.products.settings.products')}</small></span>
                </label>
              ))}</div>
            </div>
            <div>
              <h4>{tx('ui.products.coupons.products')}</h4>
              <div className="xo-coupon-checks">{scopeProducts.map((product) => (
                <label key={product.id}>
                  <input type="checkbox" checked={form.productIds.includes(product.id)} disabled={locked}
                    onChange={() => toggleId('productIds', product.id)} />
                  <span><strong>{product.name}</strong><small>{product.code}</small></span>
                </label>
              ))}</div>
            </div>
          </div>}
        </section>

        <section className="xo-pm-card">
          <h3>{tx('ui.products.coupons.limits')}</h3>
          <div className="xo-coupon-limits">
            <Field id="coupon-maximum" label={tx('ui.products.coupons.maximum')}>
              <input {...fieldAria('coupon-maximum')} type="number" min="1" step="1" value={form.maximum}
                disabled={locked} placeholder={tx('ui.products.coupons.unlimited')}
                onChange={(event) => set('maximum', event.target.value)} />
            </Field>
            <Field id="coupon-starts" label={tx('ui.products.coupons.starts')}>
              <input {...fieldAria('coupon-starts')} type="date" value={form.startsOn} disabled={locked}
                onChange={(event) => set('startsOn', event.target.value)} />
            </Field>
            <Field id="coupon-ends" label={tx('ui.products.coupons.expires')}>
              <input {...fieldAria('coupon-ends')} type="date" value={form.endsOn} min={form.startsOn || undefined}
                disabled={locked} onChange={(event) => set('endsOn', event.target.value)} />
            </Field>
          </div>
        </section>

        <section className="xo-pm-card">
          <div className="xo-pm-section-head">
            <div><h3>{tx('ui.products.coupons.markets')}</h3><p>{currencySummary}</p></div>
            {form.discountType === 'percent' && (
              <button type="button" className="xo-btn-2" onClick={() => setCurrenciesOpen((open) => !open)}>
                {currenciesOpen ? tx('ui.products.coupons.done') : tx('ui.products.coupons.change')}
              </button>
            )}
          </div>
          {showCurrencies && <>
            <div className="xo-coupon-currency-bar">
              <p className="xo-pm-hint">{fixed
                ? tx('ui.products.coupons.fixed_blank') : tx('ui.products.coupons.markets_detail')}</p>
              {!fixed && form.markets.length < active.length && (
                <button type="button" className="xo-link" disabled={locked} onClick={addAllCurrencies}>
                  {tx('ui.products.coupons.add_all_currencies')}</button>
              )}
            </div>
            <div className={`xo-coupon-currencies is-${form.discountType}`}>
              <div className="xo-coupon-currency is-head" aria-hidden="true">
                <span />
                {fixed && <span>{tx('ui.products.coupons.discount_amount')}</span>}
                <span>{tx('ui.products.coupons.minimum')}</span>
              </div>
              {active.map((currency) => {
                const row = form.markets.find((market) => market.currency === currency.code);
                const on = fixed ? Boolean(row && row.amount !== '') : Boolean(row);
                const step = 1 / (10 ** currency.minorUnit);
                return <div className={`xo-coupon-currency${on ? ' is-on' : ''}`} key={currency.code}>
                  <label className="xo-coupon-currency-name">
                    {!fixed && <input type="checkbox" checked={on} disabled={locked}
                      onChange={() => toggleCurrency(currency)} />}
                    <strong>{currency.code}</strong><span>{currency.name}</span>
                  </label>
                  {fixed && <input type="number" min={step} step={step} value={row?.amount ?? ''} disabled={locked}
                    aria-label={`${tx('ui.products.coupons.discount_amount')} ${currency.code}`}
                    onChange={(event) => changeMarket(currency.code, { amount: event.target.value })} />}
                  <input type="number" min="0" step={step} value={row?.minimum ?? ''} placeholder="0"
                    disabled={locked || (!fixed && !row)}
                    aria-label={`${tx('ui.products.coupons.minimum')} ${currency.code}`}
                    onChange={(event) => changeMarket(currency.code, { minimum: event.target.value })} />
                </div>;
              })}
            </div>
          </>}
        </section>

        <section className="xo-pm-card">
          <div className="xo-pm-section-head">
            <div><h3>{tx('ui.products.coupons.quantity_rules')}</h3>
              <p>{form.requirements.length === 0
                ? tx('ui.products.coupons.no_rules') : tx('ui.products.coupons.quantity_rules_detail')}</p></div>
            {mayManage && (
              <button type="button" className="xo-btn-2" disabled={locked || form.requirements.length >= 20}
                onClick={() => set('requirements', [...form.requirements,
                  { type: 'cart_quantity', minimumQuantity: '2', productId: null, categoryId: null }])}>
                <Plus aria-hidden="true" />{tx('ui.products.coupons.add_rule')}</button>
            )}
          </div>
          {form.requirements.map((rule, index) => <div className="xo-coupon-requirement" key={index}>
            <XomicsSelect value={rule.type} disabled={locked} aria-label={tx('ui.products.coupons.quantity_rules')}
              onChange={(event) => changeRule(index, {
                type: event.target.value,
                productId: event.target.value === 'product_quantity'
                  ? products.find((product) => product.isActive)?.id ?? null : null,
                categoryId: event.target.value === 'category_quantity'
                  ? ruleCategories(null)[0]?.id ?? null : null,
              })}>
              <option value="cart_quantity">{tx('ui.products.coupons.rule_cart')}</option>
              <option value="category_quantity">{tx('ui.products.coupons.rule_category')}</option>
              <option value="product_quantity">{tx('ui.products.coupons.rule_product')}</option>
            </XomicsSelect>
            {rule.type === 'category_quantity' && <XomicsSelect value={rule.categoryId ?? ''} disabled={locked}
              aria-label={tx('ui.products.coupons.rule_category')}
              onChange={(event) => changeRule(index, { categoryId: Number(event.target.value) })}>
              {ruleCategories(rule.categoryId).map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}</XomicsSelect>}
            {rule.type === 'product_quantity' && <XomicsSelect value={rule.productId ?? ''} disabled={locked}
              aria-label={tx('ui.products.coupons.rule_product')}
              onChange={(event) => changeRule(index, { productId: Number(event.target.value) })}>
              {products.filter((product) => product.isActive || product.id === rule.productId).map((product) => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}</XomicsSelect>}
            {rule.type === 'cart_quantity' && <span />}
            <label><span>{tx('ui.products.coupons.minimum_quantity')}</span>
              <input type="number" min="1" max="10000" step="1" value={rule.minimumQuantity} disabled={locked}
                onChange={(event) => changeRule(index, { minimumQuantity: event.target.value })} /></label>
            {mayManage && <button type="button" className="xo-icon-btn" disabled={locked}
              aria-label={tx('ui.products.coupons.remove_rule')}
              onClick={() => set('requirements', form.requirements.filter((_, row) => row !== index))}>
              <Minus aria-hidden="true" /></button>}
          </div>)}
        </section>

        {mayManage && (dirty || !coupon) && (
          <div className="xo-pm-savebar">
            <span>{coupon ? tx('ui.products.detail.unsaved') : tx('ui.products.coupons.no_delete')}</span>
            <div>
              <button type="button" className="xo-btn-2" disabled={busy}
                onClick={() => (coupon ? setForm(initial) : onClose())}>
                {coupon ? tx('ui.products.detail.discard') : tx('ui.products.coupons.cancel')}</button>
              <button type="submit" className="xo-btn" disabled={busy}>
                {busy ? tx('ui.products.coupons.saving') : tx('ui.products.coupons.save')}</button>
            </div>
          </div>
        )}
      </form>
    </section>
  </div>;
}

export default function ProductCoupons({ products, categories, currencies, mayManage }) {
  const load = useCallback(() => loadCoupons(), []);
  const list = useLiveResource(['coupons'], load);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(undefined);
  const [notice, setNotice] = useState('');
  const coupons = useMemo(() => list.data ?? [], [list.data]);
  const productNames = useMemo(() => new Map(products.map((product) => [product.id, product.name])), [products]);
  const categoryNames = useMemo(() => new Map(categories.map((category) => [
    category.id, category.name,
  ])), [categories]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle === '' ? coupons : coupons.filter((coupon) => (
      `${coupon.code} ${coupon.name}`.toLowerCase().includes(needle)
    ));
  }, [coupons, query]);
  // undefined: no sheet; null: a new coupon; an id: that coupon.
  const opened = openId === undefined || openId === null
    ? openId : coupons.find((coupon) => coupon.id === openId) ?? undefined;

  if (list.loading && list.data === null) return <Loading label={tx('ui.products.coupons.loading')} />;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;

  // The page's head is drawn here, where the button that adds a coupon has what it opens.
  return <>
    <Head title={tx('ui.products.coupons.title')} subtitle={tx('ui.products.coupons.page_detail')}>
      {mayManage && <button type="button" className="xo-btn" onClick={() => { setNotice(''); setOpenId(null); }}>
        <Plus aria-hidden="true" />{tx('ui.products.coupons.new')}</button>}
    </Head>
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={query} onChange={setQuery}
          label={tx('ui.products.coupons.search')} placeholder={tx('ui.products.coupons.search')} />
      </div>
    </div>
    <section className="xo-coupons-panel">
    {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
    {shown.length === 0 ? <Empty title={tx('ui.products.coupons.empty')}
      detail={tx('ui.products.coupons.empty_detail')} /> : <div className="xo-coupon-tiles">
      {shown.map((coupon) => <button type="button" key={coupon.id}
        className={`xo-coupon-tile${coupon.isActive ? '' : ' is-off'}`} onClick={() => setOpenId(coupon.id)}>
        <span className="xo-coupon-tile-head">
          <span className="xo-coupon-tile-code"><TicketPercent aria-hidden="true" />{coupon.code}</span>
          <Pill tone={coupon.isActive ? 'ok' : 'flat'}>{coupon.isActive
            ? tx('ui.products.coupons.enabled') : tx('ui.products.coupons.disabled')}</Pill>
        </span>
        <strong className="xo-coupon-tile-value">{discountLabel(coupon, currencies)}</strong>
        <span className="xo-coupon-tile-name">{coupon.name}</span>
        <span className="xo-coupon-tile-scope">{scopeLabel(coupon, productNames, categoryNames)}</span>
        <span className="xo-coupon-tile-foot">
          <span>{tx('ui.products.coupons.usage')} <b>{usageLabel(coupon)}</b></span>
          <span>{coupon.expiresAt === null ? tx('ui.products.coupons.no_end')
            : tx('ui.products.coupons.ends_on', { date: dayLabel(coupon.expiresAt, { last: true }) })}</span>
        </span>
      </button>)}</div>}
    {opened !== undefined && <CouponSheet key={opened?.id ?? 'new'} coupon={opened} products={products}
      categories={categories} currencies={currencies} mayManage={mayManage}
      onClose={() => setOpenId(undefined)} onSwitched={list.refresh}
      onSaved={(message) => { setOpenId(undefined); setNotice(message); list.refresh(); }} />}
    </section>
  </>;
}
